import { c as _c } from "react/compiler-runtime";
import Button from "@components/blocks/Button";
import NumberInput from "@components/hookform/NumberInput";
import ControlledRadioCardGroup from "@components/hookform/RadioCardGroup";
import ControlledSelect from "@components/hookform/Select";
import Section from "@components/Section";
import SimpleRichText from "@components/SimpleRichText";
import { formatMoney } from "@justhome/common";
import { Title } from "@justhome/react-ui";
import { ComponentSectionsPurchaseCostCalculator } from "@lib/generated/graphql";
import { useCountAnimation } from "@lib/hooks";
import useLanguage from "@lib/hooks/useLanguage";
import clsx from "clsx";
import { useTranslation } from "next-i18next";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { calculateNotaryCosts, CalculationResult, ProjectType, ResponsibleParty } from "./calculations";
interface NotaryCostCalculatorProps {
  data: ComponentSectionsPurchaseCostCalculator;
  index: number;
  fullWidth?: boolean;
}
interface NotaryCostForm {
  projectType: ProjectType;
  purchasePrice?: number;
  mortgageAmount?: number;
  useNotaryAccount?: boolean;
  requireRankCertificate?: boolean;
  responsibleParty?: ResponsibleParty;
  bankHasDraft?: boolean;
}
interface ResultRowProps {
  label: string;
  value: number;
  isTotal?: boolean;
  isSubtotal?: boolean;
}
const ResultRow: FC<ResultRowProps> = t0 => {
  const $ = _c(13);
  const {
    label,
    value,
    isTotal: t1,
    isSubtotal: t2
  } = t0;
  const isTotal = t1 === undefined ? false : t1;
  const isSubtotal = t2 === undefined ? false : t2;
  const locale = useLanguage();
  const animatedValue = useCountAnimation(value);
  let className = "";
  let labelClassName = "text-neutral-675/60";
  let valueClassName = "";
  if (isSubtotal) {
    className = "font-book";
    labelClassName = "";
  } else {
    if (isTotal) {
      className = "text-h4";
      labelClassName = "font-book";
      valueClassName = "font-book";
    }
  }
  const t3 = `flex justify-between ${className}`;
  let t4;
  if ($[0] !== label || $[1] !== labelClassName) {
    t4 = <span className={labelClassName}>{label}</span>;
    $[0] = label;
    $[1] = labelClassName;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = valueClassName;
  let t6;
  if ($[3] !== animatedValue || $[4] !== locale) {
    t6 = formatMoney(animatedValue, false, locale, true);
    $[3] = animatedValue;
    $[4] = locale;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t6 || $[7] !== valueClassName) {
    t7 = <span className={t5}>{t6}</span>;
    $[6] = t6;
    $[7] = valueClassName;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t3 || $[10] !== t4 || $[11] !== t7) {
    t8 = <div className={t3}>{t4}{t7}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
};
const getProjectTypeOptions = (t: any) => [{
  value: ProjectType.KAUF,
  label: t("project_types.purchase")
}, {
  value: ProjectType.KAUF_GRUNDSCHULDABTRETUNG,
  label: t("project_types.purchase_assignment")
}, {
  value: ProjectType.KAUF_GRUNDSCHULDBESTELLUNG,
  label: t("project_types.purchase_order")
}, {
  value: ProjectType.GRUNDSCHULDABTRETUNG,
  label: t("project_types.assignment")
}, {
  value: ProjectType.GRUNDSCHULDBESTELLUNG,
  label: t("project_types.order")
}, {
  value: ProjectType.LOESCHUNG,
  label: t("project_types.deletion")
}];
const getResponsiblePartyOptions = (t: any) => [{
  value: ResponsibleParty.BANK,
  label: t("responsible_parties.bank")
}, {
  value: ResponsibleParty.EIGENTUEMER,
  label: t("responsible_parties.owner")
}, {
  value: ResponsibleParty.NOTAR,
  label: t("responsible_parties.notary")
}];
const getYesNoOptions = (t: any) => [{
  value: true,
  label: t("yes_no.yes")
}, {
  value: false,
  label: t("yes_no.no")
}];
const defaultValues: NotaryCostForm = {
  projectType: ProjectType.KAUF,
  purchasePrice: 100_000,
  mortgageAmount: 100_000,
  useNotaryAccount: false,
  requireRankCertificate: false,
  responsibleParty: ResponsibleParty.NOTAR,
  bankHasDraft: false
};
export const NotaryCostCalculatorSection = t0 => {
  const $ = _c(6);
  const {
    data,
    index
  } = t0;
  let t1;
  if ($[0] !== data || $[1] !== index) {
    t1 = <NotaryCostCalculator data={data} index={index} />;
    $[0] = data;
    $[1] = index;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== data || $[4] !== t1) {
    t2 = <Section section={data} className="my-3xl">{t1}</Section>;
    $[3] = data;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export const NotaryCostCalculatorArticleSection = t0 => {
  const $ = _c(3);
  const {
    data,
    index
  } = t0;
  let t1;
  if ($[0] !== data || $[1] !== index) {
    t1 = <NotaryCostCalculator data={data} index={index} />;
    $[0] = data;
    $[1] = index;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const NotaryCostCalculator: FC<NotaryCostCalculatorProps> = ({
  data,
  index,
  fullWidth = false
}) => {
  const {
    t
  } = useTranslation("notary-cost-calculator");
  const {
    control,
    watch,
    getValues
  } = useForm<NotaryCostForm>({
    defaultValues: {
      ...defaultValues
    },
    mode: "onChange"
  });
  const [result, setResult] = useState<CalculationResult | null>(null);
  const projectType = watch("projectType");
  const useNotaryAccount = watch("useNotaryAccount");
  useEffect(() => {
    let timeoutId: number;
    const performCalculation = (values: NotaryCostForm) => {
      if (values.purchasePrice === null || values.mortgageAmount === null) {
        return;
      }
      const result_0 = calculateNotaryCosts({
        projectType: values.projectType ?? ProjectType.KAUF,
        purchasePrice: values.purchasePrice,
        mortgageAmount: values.mortgageAmount,
        useNotaryAccount: values.useNotaryAccount,
        requireRankCertificate: values.requireRankCertificate,
        responsibleParty: values.responsibleParty,
        bankHasDraft: values.bankHasDraft
      });
      setResult(result_0);
    };
    const subscription = watch(values_0 => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        performCalculation(values_0 as NotaryCostForm);
      }, 300);
    });
    performCalculation(getValues());
    return () => {
      window.clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [watch, projectType, useNotaryAccount, getValues]);
  const showPurchasePrice = [ProjectType.KAUF, ProjectType.KAUF_GRUNDSCHULDABTRETUNG, ProjectType.KAUF_GRUNDSCHULDBESTELLUNG].includes(projectType);
  const showMortgageAmount = [ProjectType.KAUF_GRUNDSCHULDABTRETUNG, ProjectType.KAUF_GRUNDSCHULDBESTELLUNG, ProjectType.GRUNDSCHULDABTRETUNG, ProjectType.GRUNDSCHULDBESTELLUNG, ProjectType.LOESCHUNG].includes(projectType);
  const showNotaryAccount = [ProjectType.KAUF_GRUNDSCHULDABTRETUNG, ProjectType.KAUF_GRUNDSCHULDBESTELLUNG].includes(projectType);
  const showRankCertificate = projectType === ProjectType.KAUF_GRUNDSCHULDBESTELLUNG && useNotaryAccount === false;
  const showResponsibleParty = projectType === ProjectType.GRUNDSCHULDABTRETUNG;
  const showBankDraft = projectType === ProjectType.LOESCHUNG;
  const projectTypeOptions = getProjectTypeOptions(t);
  const responsiblePartyOptions = getResponsiblePartyOptions(t);
  const yesNoOptions = getYesNoOptions(t);
  return <div>
      {data.title && <div className="max-w-[820px] m-auto text-center mb-m">
          <Title className="text-d3 m:text-d2" as={index === 0 ? "h1" : "h2"}>
            {data.title}
          </Title>
        </div>}
      {data.description && <SimpleRichText className="text-body-1 text-neutral-575 mt-3s font-normal max-w-[680px] m-auto text-center mb-m">
          {data.description}
        </SimpleRichText>}
      <div className="grid grid-cols-12">
        <div className={clsx("col-span-12", {
        "s:col-span-10 s:col-start-2 m:col-span-10 m:col-start-2 l:col-span-8 l:col-start-3": !fullWidth
      })}>
          <div className="grid grid-cols-12 gap-m">
            {/* Form Section */}
            <div className="col-span-12 m:col-span-4 l:col-span-5 space-y-2s m:pt-m">
              <ControlledSelect name="projectType" control={control} label={t("project_type_label")} placeholder={t("project_type_placeholder")} options={projectTypeOptions} />

              {showPurchasePrice && <NumberInput autoDefaultValue={defaultValues.purchasePrice} name="purchasePrice" control={control} label={t("purchase_price_label")} suffix="€" />}

              {showMortgageAmount && <NumberInput autoDefaultValue={defaultValues.mortgageAmount} name="mortgageAmount" control={control} label={t("mortgage_amount_label")} suffix="€" />}

              {showNotaryAccount && <ControlledRadioCardGroup name="useNotaryAccount" control={control} label={t("notary_account_label")} options={yesNoOptions} />}

              {showRankCertificate && <ControlledRadioCardGroup name="requireRankCertificate" control={control} label={t("rank_certificate_label")} options={yesNoOptions} />}

              {showResponsibleParty && <ControlledSelect name="responsibleParty" control={control} label={t("responsible_party_label")} placeholder={t("responsible_party_placeholder")} options={responsiblePartyOptions} />}

              {showBankDraft && <ControlledRadioCardGroup name="bankHasDraft" control={control} label={t("bank_draft_label")} options={yesNoOptions} />}
            </div>

            {/* Results Section */}
            <div className="col-span-12 m:col-span-8 l:col-span-7 flex flex-col">
              {result && result.totalCosts > 0 && <div className="bg-neutral-12 rounded-m p-m border border-neutral-50">
                  <h3 className="text-h3 mb-s text-align-top">
                    {t("result_title")}
                  </h3>

                  <div>
                    <div className="space-y-2xs">
                      <div className="font-semibold text-neutral-700 mt-s">
                        {t("notary_fees_title")}
                      </div>

                      {result.notaryFees.contractExecutionFees > 0 && <ResultRow label={t("notary_fees.contract_execution")} value={result.notaryFees.contractExecutionFees} />}

                      {result.notaryFees.purchaseContractCertification > 0 && <ResultRow label={t("notary_fees.purchase_contract_certification")} value={result.notaryFees.purchaseContractCertification} />}

                      {result.notaryFees.mortgageCertification > 0 && <ResultRow label={t("notary_fees.mortgage_certification")} value={result.notaryFees.mortgageCertification} />}

                      {result.notaryFees.rankCertificate > 0 && <ResultRow label={t("notary_fees.rank_certificate")} value={result.notaryFees.rankCertificate} />}

                      {result.notaryFees.signatureAuthentication > 0 && <ResultRow label={t("notary_fees.signature_authentication")} value={result.notaryFees.signatureAuthentication} />}

                      {result.notaryFees.otherProcessingActivities > 0 && <ResultRow label={t("notary_fees.other_processing")} value={result.notaryFees.otherProcessingActivities} />}

                      {result.notaryFees.notaryAccountProcessing > 0 && <ResultRow label={t("notary_fees.notary_account_processing")} value={result.notaryFees.notaryAccountProcessing} />}

                      {result.notarySubtotal > 0 && <>
                          <ResultRow label={t("subtotal")} value={result.notarySubtotal} isSubtotal />
                          <ResultRow label={t("vat_addition")} value={result.notaryVAT} />
                          <ResultRow label={t("notary_fees_total")} value={result.totalNotaryFees} isSubtotal />
                        </>}
                    </div>

                    {result.totalLandRegistryFees > 0 && <div className="space-y-2xs mt-2s">
                        <div className="font-semibold text-neutral-700 mt-3s">
                          {t("land_registry_fees_title")}
                        </div>

                        {result.landRegistryFees.transferReservation > 0 && <ResultRow label={t("land_registry_fees.transfer_reservation")} value={result.landRegistryFees.transferReservation} />}

                        {result.landRegistryFees.ownerTransfer > 0 && <ResultRow label={t("land_registry_fees.owner_transfer")} value={result.landRegistryFees.ownerTransfer} />}

                        {result.landRegistryFees.landRegisterChanges > 0 && <ResultRow label={t("land_registry_fees.land_register_changes")} value={result.landRegistryFees.landRegisterChanges} />}

                        {result.landRegistryFees.mortgageEntry > 0 && <ResultRow label={t("land_registry_fees.mortgage_entry")} value={result.landRegistryFees.mortgageEntry} />}

                        {result.landRegistryFees.landRegisterDeletion > 0 && <ResultRow label={t("land_registry_fees.land_register_deletion")} value={result.landRegistryFees.landRegisterDeletion} />}

                        <ResultRow label={t("land_registry_fees_total")} value={result.totalLandRegistryFees} isSubtotal />
                      </div>}

                    <hr className="border-neutral-100 my-s" />
                    <ResultRow label={t("total_costs")} value={result.totalCosts} isTotal />
                  </div>

                  <div className="mt-m text-body-2 text-neutral-675/60">
                    <p>{t("disclaimer")}</p>
                  </div>

                  {data.button && <div className="mt-m">
                      <Button {...data.button} fullWidth />
                    </div>}
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
};